// packages/ui/src/CodeEditorField.tsx
import React, { useId, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import CodeMirror, { ReactCodeMirrorProps } from '@uiw/react-codemirror';
import { EditorState, Extension } from '@codemirror/state';
import {
  EditorView,
  lineNumbers,
  highlightActiveLineGutter,
  highlightSpecialChars,
  drawSelection,
  dropCursor,
  highlightActiveLine,
  ViewPlugin,
  PluginValue,
  keymap
} from '@codemirror/view';
import { indentOnInput, bracketMatching, foldGutter, foldKeymap, syntaxHighlighting, HighlightStyle } from '@codemirror/language';
import { history, defaultKeymap, historyKeymap } from '@codemirror/commands';
import { searchKeymap, highlightSelectionMatches } from '@codemirror/search';
import { autocompletion, completionKeymap, closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { lintKeymap } from '@codemirror/lint';

// Language Support
import { javascript } from '@codemirror/lang-javascript';
import { python } from '@codemirror/lang-python';
import { json } from '@codemirror/lang-json';
import { css } from '@codemirror/lang-css';
import { html } from '@codemirror/lang-html';

// Example Theme
import { oneDark } from '@codemirror/theme-one-dark';

import { IconButton } from '../components/IconButton';
import { Copy, Check } from 'lucide-react';

const defaultSyntaxHighlighting = HighlightStyle.define([]);

const editorFrameHoverFocusStyles =
  'hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] hover:-translate-x-px hover:-translate-y-px focus-within:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.9)] focus-within:-translate-x-px focus-within:-translate-y-px';
const errorEditorFrameHoverFocusStyles =
  'hover:shadow-[2px_2px_0px_0px_rgba(220,38,38,0.9)] hover:-translate-x-px hover:-translate-y-px focus-within:shadow-[2px_2px_0px_0px_rgba(220,38,38,0.9)] focus-within:-translate-x-px focus-within:-translate-y-px';
type CodeEditorChangeViewUpdate = Parameters<NonNullable<ReactCodeMirrorProps['onChange']>>[1];

export interface CodeEditorFieldProps {
  label?: string;
  labelKey?: string;
  value: string;
  onChange: (code: string, viewUpdate?: CodeEditorChangeViewUpdate) => void;
  language?: 'plaintext' | 'javascript' | 'typescript' | 'python' | 'json' | 'css' | 'html';
  theme?: 'light' | 'dark' | Extension;
  descriptionText?: string;
  descriptionTextKey?: string;
  errorText?: string;
  errorTextKey?: string;
  disabled?: boolean;
  readOnly?: boolean;
  id?: string;
  name?: string;
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  showLineNumbers?: boolean;
  enableBasicAutocompletion?: boolean;
  containerClassName?: string;
  labelClassName?: string;
  editorWrapperClassName?: string; // This will now be for the CodeMirror editor's direct container
  showCopyButton?: boolean;
  applyCustomScrollbar?: boolean;
  cmProps?: Omit<ReactCodeMirrorProps, 'value' | 'onChange' | 'extensions' | 'editable' | 'readOnly' | 'theme' | 'height' | 'minHeight' | 'maxHeight'>;
}

function setAriaLabelPlugin(label: string): Extension {
  return ViewPlugin.fromClass(
    class implements PluginValue {
      private view: EditorView;
      constructor(view: EditorView) {
        this.view = view;
        this.view.contentDOM.setAttribute('aria-label', label);
      }
      update() { }
      destroy() {
        this.view.contentDOM.removeAttribute('aria-label');
      }
    }
  );
}

export const CodeEditorField: React.FC<CodeEditorFieldProps> = ({
  label,
  labelKey,
  value,
  onChange,
  language = 'plaintext',
  theme = 'light',
  descriptionText,
  descriptionTextKey,
  errorText,
  errorTextKey,
  disabled = false,
  readOnly = false,
  id,
  name,
  height = '300px',
  minHeight,
  maxHeight,
  showLineNumbers = true,
  enableBasicAutocompletion = true,
  containerClassName,
  labelClassName,
  editorWrapperClassName,
  showCopyButton = true,
  applyCustomScrollbar = false,
  cmProps = {},
}) => {
  const { t } = useTranslation();
  const autoId = useId();
  const fieldId = id || autoId;
  const fieldName = name || fieldId;

  const [isCopied, setIsCopied] = useState(false);

  const resolvedLabel = labelKey ? t(labelKey) : label;
  const resolvedDescriptionText = descriptionTextKey ? t(descriptionTextKey) : descriptionText;
  const resolvedErrorText = errorTextKey ? t(errorTextKey) : errorText;
  const hasError = !!resolvedErrorText;

  const handleCopy = useCallback(async () => {
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(value);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    }
  }, [value]);

  const extensions = useMemo(() => {
    const languageExtensionsList: Record<NonNullable<CodeEditorFieldProps['language']>, () => Extension> = {
      javascript: () => javascript({ jsx: true, typescript: language === 'typescript' }),
      typescript: () => javascript({ jsx: true, typescript: true }),
      python: () => python(),
      json: () => json(),
      css: () => css(),
      html: () => html(),
      plaintext: () => [],
    };
    const selectedLanguageExtension = languageExtensionsList[language]
      ? languageExtensionsList[language]()
      : [];
    const exts: Extension[] = [
      setAriaLabelPlugin(resolvedLabel || 'Code editor'),
      EditorView.lineWrapping,
      highlightSpecialChars(),
      history(),
      drawSelection(),
      dropCursor(),
      EditorState.allowMultipleSelections.of(true),
      indentOnInput(),
      syntaxHighlighting(defaultSyntaxHighlighting, { fallback: true }),
      bracketMatching(),
      closeBrackets(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      keymap.of([
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...searchKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...completionKeymap,
        ...lintKeymap,
      ]),
    ];
    if (showLineNumbers) {
      exts.push(lineNumbers(), foldGutter(), highlightActiveLineGutter());
    }
    if (enableBasicAutocompletion) {
      exts.push(autocompletion());
    }
    if (selectedLanguageExtension) {
      exts.push(selectedLanguageExtension);
    }

    let editorTheme: Extension = EditorView.theme({});
    if (theme === 'dark' && typeof theme === 'string') {
      editorTheme = oneDark;
    } else if (typeof theme !== 'string') {
      editorTheme = theme;
    }
    exts.push(editorTheme);

    if (applyCustomScrollbar) {
      exts.push(EditorView.theme({
        "&": {},
        ".cm-scroller": {
          fontFamily: "inherit",
          overflowX: "auto",
          scrollbarWidth: "thin",
          scrollbarColor: `${theme === 'dark' ? "rgba(100,100,100,0.5)" : "rgba(100,100,100,0.4)"} ${theme === 'dark' ? "rgba(40,40,40,0.3)" : "rgba(230,230,230,0.3)"}`,
        },
        ".cm-scroller::-webkit-scrollbar": {
          width: "10px",
          height: "10px",
        },
        ".cm-scroller::-webkit-scrollbar-track": {
          backgroundColor: theme === 'dark' ? "rgba(40,40,40,0.3)" : "rgba(230,230,230,0.3)",
          borderRadius: "5px",
        },
        ".cm-scroller::-webkit-scrollbar-thumb": {
          backgroundColor: theme === 'dark' ? "rgba(100,100,100,0.5)" : "rgba(100,100,100,0.4)",
          borderRadius: "5px",
          border: `2px solid ${theme === 'dark' ? "rgba(40,40,40,0.3)" : "rgba(230,230,230,0.3)"}`,
        },
        ".cm-scroller::-webkit-scrollbar-corner": {
          backgroundColor: "transparent",
        },
      }, { dark: theme === 'dark' }));
    }
    return exts;
  }, [applyCustomScrollbar, enableBasicAutocompletion, language, resolvedLabel, showLineNumbers, theme]);


  return (
    <div className={clsx('w-full', containerClassName)}>
      {/* Container for Label and Copy Button */}
      {(resolvedLabel || (showCopyButton && !disabled && !readOnly)) && (
        <div className="flex justify-between items-center mb-1">
          {resolvedLabel && (
            <label
              className={clsx(
                'block text-sm font-medium text-gray-700',
                '[.theme-liquid-glass_&]:text-white/80',
                labelClassName
              )}
            >
              {resolvedLabel}
            </label>
          )}
          {showCopyButton && !disabled && !readOnly && (
            <IconButton
              icon={isCopied ? <Check size={16} /> : <Copy size={16} />}
              onClick={handleCopy}
              ariaLabel={isCopied ? 'Copied!' : 'Copy code'}
              tooltip={isCopied ? 'Copied!' : 'Copy code'}
              variant="ghost"
              size="sm"
              className={clsx(
                // No absolute positioning needed here
                theme === 'dark' ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700 active:bg-gray-600' : 'text-gray-500 hover:text-gray-700 hover:bg-slate-100 active:bg-slate-200',
                isCopied && (theme === 'dark' ? '!bg-emerald-700 !text-white' : '!bg-emerald-500 !text-white')
              )}
              applyNeobrutalistEffects={true} // Keep it simple next to the label
            />
          )}
        </div>
      )}

      {/* This div is the Neobrutalist frame for the editor content */}
      <div
        className={clsx(
          'relative w-full rounded-lg border-2 overflow-hidden', // overflow-hidden is fine here as CM content is inside
          theme === 'dark' ? 'border-gray-600' : 'border-black',
          hasError && (theme === 'dark' ? 'border-red-400' : 'border-red-500'),
          'transition-all duration-150 ease-in-out',
          '[.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:backdrop-blur-xl',
          !disabled && !readOnly && !hasError && (theme === 'dark' ? editorFrameHoverFocusStyles.replace('rgba(0,0,0,0.9)', 'rgba(150,150,150,0.7)') : editorFrameHoverFocusStyles),
          !disabled && !readOnly && hasError && (theme === 'dark' ? errorEditorFrameHoverFocusStyles.replace('rgba(220,38,38,0.9)', 'rgba(255,100,100,0.8)') : errorEditorFrameHoverFocusStyles),
          (disabled || readOnly) && (theme === 'dark' ? 'opacity-70 bg-gray-700 border-gray-500' : 'opacity-70 bg-gray-100 border-gray-300'),
          editorWrapperClassName // This class applies to the frame
        )}
      >
        <CodeMirror
          id={fieldId}
          value={value}
          onChange={onChange}
          extensions={extensions}
          editable={!disabled && !readOnly}
          readOnly={readOnly || disabled}
          height={height}
          minHeight={minHeight}
          maxHeight={maxHeight}
          theme={typeof theme === 'string' && theme === 'dark' ? oneDark : (typeof theme === 'string' ? 'light' : theme)}
          // Background is handled by the theme (e.g., oneDark) or CM default.
          // No specific className needed for background here unless overriding theme.
          {...cmProps}
        />
      </div>

      {resolvedErrorText && (
        <p id={`${fieldId}-error`} className="mt-1.5 text-xs text-red-600 [.theme-liquid-glass_&]:text-rose-400">
          {resolvedErrorText}
        </p>
      )}
      {resolvedDescriptionText && !hasError && (
        <p id={`${fieldId}-description`} className="mt-1.5 text-xs text-gray-500 [.theme-liquid-glass_&]:text-white/50">
          {resolvedDescriptionText}
        </p>
      )}
      {name && <input type="hidden" name={fieldName} value={value} />}
    </div>
  );
};
